import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useToast } from '@/components/feedback/toast';
import { resolveLandingRoute } from '@/config/routes';
import { useAuth } from '@/providers/auth-provider';
import type { Role } from '@/types';

/** Role guard (ADR-0014, amended by ADR-0028): on a role miss, redirect to the role's own landing
 *  route and fire an access-denied toast. The destination is role-dependent because the Dashboard
 *  is itself gated — a fixed redirect there would loop for anyone the Dashboard also refuses.
 *  `pageName` names the module in the message; omit it for a generic message. This is a UX
 *  affordance on top of backend-driven RBAC (ADR-0001), not a replacement. */
export function RequireRole({ roles, pageName }: { roles: Role[]; pageName?: string }) {
  const { user } = useAuth();
  const toast = useToast();
  const ok = user?.role ? roles.includes(user.role) : false;

  useEffect(() => {
    if (!ok) {
      toast.error({
        title: `Access Denied. You do not have permission to access ${pageName ?? 'this page'}.`,
        key: 'access-denied',
      });
    }
  }, [ok, pageName, toast]);

  if (!ok) return <Navigate to={resolveLandingRoute(user?.role)} replace />;
  return <Outlet />;
}
